import type { Error as Auth0Error } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import { useResetPasswordErrorManager } from "../hooks/useResetPasswordErrorManager";
import {
  buildFallbackLoginLink,
  rebaseLinkToCurrentOrigin,
} from "../utils/helpers/urlUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

function ResetPasswordErrorScreen() {
  const {
    errors,
    texts,
    prefilledIdentifier,
    loginLink,
    transactionState,
    resetPasswordLink,
  } = useResetPasswordErrorManager();

  const sdkErrors = (errors || []) as Auth0Error[];
  const generalErrors = sdkErrors.filter((error) => !error.field);
  const localizedLoginLink = loginLink && rebaseLinkToCurrentOrigin(loginLink);
  const fallbackLoginLink = buildFallbackLoginLink(transactionState);
  const resolvedLoginLink = localizedLoginLink || fallbackLoginLink;
  const localizedResetPasswordLink =
    resetPasswordLink && rebaseLinkToCurrentOrigin(resetPasswordLink);

  const headingText = texts?.title || "Unable to reset password";
  const descriptionText =
    texts?.description ||
    "We could not complete your password reset. Try again or return to sign in.";
  const retryText = texts?.buttonText || "Try again";
  const backToLoginText =
    texts?.backToLoginText || texts?.backText || "Back to sign in";

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:block md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <div className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12">
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">{headingText}</p>
            <p className="mt-2 text-sm text-ink-2">{descriptionText}</p>
            {prefilledIdentifier && (
              <p className="mt-1 text-sm font-semibold text-ink">
                {prefilledIdentifier}
              </p>
            )}
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`resetpassworderror-general-error-${index}`}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {localizedResetPasswordLink ? (
            <a
              href={localizedResetPasswordLink}
              className="inline-flex w-full items-center justify-center rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm"
            >
              {retryText}
            </a>
          ) : null}

          <p className="text-sm text-ink-2">
            Problems signing in?{" "}
            <a
              href="https://www.butterfield.com/my-account-faq"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
            >
              Click here
            </a>
            .
          </p>

          {resolvedLoginLink ? (
            <a
              href={resolvedLoginLink}
              className="text-sm font-semibold text-ink-2 underline underline-offset-2"
            >
              {backToLoginText}
            </a>
          ) : (
            <p className="text-sm font-semibold text-ink-2">{backToLoginText}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordErrorScreen;
